import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Crown, Star, Sparkles, Upload, Trash2, Save, Video, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";

const TIER_BADGES = {
  basic: { label: "Basic", icon: Star, color: "text-white/70 bg-white/10 border-white/20" },
  medium: { label: "Medium", icon: Sparkles, color: "text-amber-300 bg-amber-300/10 border-amber-300/30" },
  elite: { label: "Elite", icon: Crown, color: "text-gold bg-gold/15 border-gold/40" },
};

export default function ProfilePage() {
  const [me, setMe] = useState(null);
  const [bio, setBio] = useState("");
  const [location, setLocation] = useState("");
  const [avatar, setAvatar] = useState("");
  const [gender, setGender] = useState("");
  const [social, setSocial] = useState({ instagram: "", twitter: "", website: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const res = await api.get("/profile/me");
      setMe(res.data);
      setBio(res.data.profile?.bio || "");
      setLocation(res.data.profile?.location || "");
      setAvatar(res.data.profile?.avatar_url || "");
      setGender(res.data.profile?.gender || "");
      setSocial({
        instagram: res.data.profile?.social_links?.instagram || "",
        twitter: res.data.profile?.social_links?.twitter || "",
        website: res.data.profile?.social_links?.website || "",
      });
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Please sign in first");
        window.location.href = "/vip";
      }
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { bio, location, avatar_url: avatar, social_links: social };
      if (gender) payload.gender = gender;
      const res = await api.patch("/profile/me", payload);
      setMe(res.data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(errorMessage(err, "Update failed"));
    } finally { setSaving(false); }
  };

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error("Max 50 MB per video"); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/profile/videos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Video uploaded");
      setMe((m) => ({ ...m, videos: [...(m.videos || []), res.data] }));
    } catch (err) {
      toast.error(errorMessage(err, "Upload failed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm("Remove this video?")) return;
    try {
      await api.delete(`/profile/videos/${videoId}`);
      setMe((m) => ({ ...m, videos: (m.videos || []).filter((v) => v.id !== videoId) }));
      toast.success("Removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!me) {
    return <div className="pt-32 min-h-screen bg-luxury-black flex items-center justify-center text-gold/70 font-body">Loading profile…</div>;
  }
  const tier = me.tier || "basic";
  const badge = TIER_BADGES[tier];
  const limits = me.tier_limits || {};
  const videoCount = (me.videos || []).length;

  return (
    <div data-testid="profile-page" className="pt-24 min-h-screen bg-luxury-black">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row items-start md:items-center gap-6 mb-10 pb-8 border-b border-white/10">
          <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-gold/40 bg-white/5 flex items-center justify-center">
            {avatar ? (<img src={avatar} alt="avatar" className="w-full h-full object-cover" />) : (
              <span className="text-gold font-heading text-3xl">{(me.name || "A").charAt(0).toUpperCase()}</span>
            )}
          </div>
          <div className="flex-1">
            <p className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-1">Your Profile</p>
            <h1 className="font-heading text-3xl text-white mb-2">{me.name}</h1>
            <div className="flex items-center gap-3">
              <span data-testid="profile-tier-badge" className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-body uppercase tracking-wider border ${badge.color}`}>
                <badge.icon size={12} /> {badge.label} Tier
              </span>
              <span className="text-white/40 text-xs font-body">
                {videoCount} / {limits.max_videos === 999 ? "∞" : limits.max_videos} videos · up to {limits.max_seconds}s each
              </span>
            </div>
          </div>
        </motion.div>

        {/* Bio + social */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          <div className="space-y-3">
            <label className="text-white/60 text-xs font-body uppercase tracking-wider">Bio</label>
            <Textarea data-testid="profile-bio" value={bio} onChange={(e) => setBio(e.target.value)}
              maxLength={500} rows={4} placeholder="Tell people who you are…"
              className="bg-white/5 border-white/15 text-white placeholder:text-white/30" />
            <label className="text-white/60 text-xs font-body uppercase tracking-wider flex items-center gap-1"><MapPin size={12} /> Location</label>
            <Input data-testid="profile-location" value={location} onChange={(e) => setLocation(e.target.value)}
              placeholder="Kingston, Jamaica" className="bg-white/5 border-white/15 text-white" />
            <label className="text-white/60 text-xs font-body uppercase tracking-wider">Avatar URL</label>
            <Input data-testid="profile-avatar" value={avatar} onChange={(e) => setAvatar(e.target.value)}
              placeholder="https://…" className="bg-white/5 border-white/15 text-white" />
            <label className="text-white/60 text-xs font-body uppercase tracking-wider">Gender</label>
            <select data-testid="profile-gender" value={gender} onChange={(e) => setGender(e.target.value)}
              className="w-full bg-white/5 border border-white/15 text-white px-3 py-2 rounded-md text-sm">
              <option value="">— Select —</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
              <option value="prefer_not_say">Prefer not to say</option>
            </select>
          </div>
          <div className="space-y-3">
            <label className="text-white/60 text-xs font-body uppercase tracking-wider">Social Links</label>
            <Input data-testid="social-instagram" value={social.instagram}
              onChange={(e) => setSocial({ ...social, instagram: e.target.value })}
              placeholder="Instagram URL" className="bg-white/5 border-white/15 text-white" />
            <Input data-testid="social-twitter" value={social.twitter}
              onChange={(e) => setSocial({ ...social, twitter: e.target.value })}
              placeholder="Twitter / X URL" className="bg-white/5 border-white/15 text-white" />
            <Input data-testid="social-website" value={social.website}
              onChange={(e) => setSocial({ ...social, website: e.target.value })}
              placeholder="Personal website" className="bg-white/5 border-white/15 text-white" />
            <button data-testid="profile-save-btn" disabled={saving} onClick={save}
              className="mt-3 w-full flex items-center justify-center gap-2 bg-gold text-luxury-black px-5 py-3 font-body text-sm uppercase tracking-wider hover:bg-gold/90 transition-colors disabled:opacity-50">
              <Save size={14} /> {saving ? "Saving…" : "Save Profile"}
            </button>
          </div>
        </div>

        {/* Videos */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-1">Promotional Videos</p>
              <h2 className="font-heading text-xl text-white">Your Videos</h2>
            </div>
            <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm"
              onChange={uploadVideo} className="hidden" data-testid="video-upload-input" />
            <button data-testid="upload-video-btn" disabled={uploading || videoCount >= (limits.max_videos || 0)}
              onClick={() => fileRef.current?.click()}
              className="flex items-center gap-2 bg-gold/15 text-gold px-4 py-2 text-xs font-body hover:bg-gold/25 transition-colors disabled:opacity-40 border border-gold/30">
              <Upload size={14} /> {uploading ? "Uploading…" : "Upload Video"}
            </button>
          </div>
          {videoCount === 0 ? (
            <div className="border border-dashed border-white/15 rounded-md p-12 text-center">
              <Video className="mx-auto mb-3 text-white/30" size={36} />
              <p className="text-white/50 font-body text-sm">No videos yet — upload your first promotional clip</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(me.videos || []).map((v) => (
                <div key={v.id} data-testid={`video-card-${v.id}`} className="relative group border border-white/10 rounded-md overflow-hidden bg-white/5">
                  <video src={`${process.env.REACT_APP_BACKEND_URL}${v.url}`} controls className="w-full aspect-video bg-black" />
                  <div className="p-3 flex items-center justify-between">
                    <span className="text-white text-xs font-body truncate">{v.title}</span>
                    <button data-testid={`delete-video-${v.id}`} onClick={() => deleteVideo(v.id)}
                      className="text-red-400/70 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tier info */}
        <div className="p-6 border border-gold/20 rounded-md bg-gold/5">
          <p className="text-gold text-xs font-body uppercase tracking-[0.2em] mb-3">Tier Benefits</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm font-body text-white/70">
            <div><span className="text-white">Basic</span> — 2 videos · 30s each</div>
            <div><span className="text-amber-300">Medium</span> — 5 videos · 60s each</div>
            <div><span className="text-gold">Elite</span> — Unlimited · 180s · Homepage Spotlight</div>
          </div>
          <p className="text-white/40 text-xs font-body mt-3">Contact admin to upgrade your tier.</p>
        </div>
      </div>
    </div>
  );
}
